import { gql } from "@urql/core";

// Queries
export const SOURCING_CANDIDATES_QUERY = gql`
  query SourcingCandidates($search: String, $categoryType: String, $productCategory: String, $department: String, $prNumber: String, $requester: String) {
    sourcingCandidates(search: $search, categoryType: $categoryType, productCategory: $productCategory, department: $department, prNumber: $prNumber, requester: $requester)
  }
`;

export const SOURCING_LIST_QUERY = gql`
  query SourcingList {
    sourcings {
      nodes {
        id
        sourcingNo
        prIds
        type
        sourcingScope
        invitationMode
        status
        totalAmount
        currency
        createdAt
        updatedAt
      }
    }
  }
`;

export const SOURCING_ONE_QUERY = gql`
  query SourcingOne($id: String!) {
    sourcing(id: $id) {
      id
      sourcingNo
      prIds
      title
      description
      type
      sourcingScope
      invitationMode
      technicalEvaluationMode
      evaluationPhase
      status
      items {
        id
        name
        description
        quantity
        unit
        unitPrice
        totalPrice
        specifications
        tags
        allocations {
          prId
          prItemId
          quantity
        }
      }
      totalAmount
      currency
      notes
      attachments {
        fileId
        originalName
        mimeType
        size
        category
        url
      }
      evaluationCriterias {
        id
        name
        type
        description
        weight
        visibleToSuppliers
        threshold
      }
      createdBy
      submittedBy
      submittedAt
      approvedBy
      openedAt
      openedBy
      approvalType
      steps {
        order
        roleSlug
        stepName
      }
      approvals {
        order
        roleSlug
        stepName
        approvedBy
        approvedOn
        remarks
        stepStatus
      }
      externalWorkflow
      awardApprovalType
      awardApprovalFlow
      awardApprovals {
        order
        roleSlug
        stepName
        approvedBy
        approvedOn
        remarks
        stepStatus
      }
      resultSubmittedBy
      resultSubmittedAt
      awardedBy
      awardedAt
      publishData
      approvalData
      flowCaseId
      flowCaseNumber
      awardLetter {
        fileId
        originalName
        mimeType
        size
        category
        url
        createdBy
      }
      awardLetters {
        fileId
        originalName
        mimeType
        size
        category
        url
        createdBy
      }
      awardAcceptanceLetter {
        fileId
        originalName
        mimeType
        size
        category
        url
        createdBy
      }
      awardAcceptanceLetters {
        fileId
        originalName
        mimeType
        size
        category
        url
        createdBy
      }
      poId
      poIds
      itemAwards {
        id
        sourcingItemId
        bidId
        supplierId
        supplierName
        quantity
        unitPrice
        totalPrice
        decision
        remarks
      }
      createdAt
      updatedAt
    }
  }
`;

export const APPROVED_PURCHASE_REQUESTS_QUERY = gql`
  query ApprovedPurchaseRequests($filter: JSONObject) {
    purchaseRequests(query: { filter: $filter }) {
      nodes {
        id
        requestNo
        status
        title 
        description
        items {
          id
          name
          description
          quantity
          requestedQty
          inSourcingQty
          awardedQty
          unit
          unitPrice
          totalPrice
          specifications
          tags
        }
        totalAmount
        currency
        attachments {
          fileId
          originalName
          mimeType
          size
          category
          url
        }
      }
    }
  }
`;

export const ASSIGNABLE_PURCHASE_REQUESTS_QUERY = gql`
  query AssignablePurchaseRequests {
    assignablePurchaseRequests {
      id
      requestNo
      status
      title
      description
      items {
        id
        name
        description
        quantity
        requestedQty
        inSourcingQty
        awardedQty
        unit
        unitPrice
        totalPrice
        specifications
        tags
      }
      totalAmount
      currency
      assignedTo
      assignedBy
      assignedOn
      attachments {
        fileId
        originalName
        mimeType
        size
        category
        url
      }
    }
  }
`;

// Mutations
export const CREATE_SOURCING_MUTATION = gql`
  mutation CreateSourcing($input: CreateSourcingInput!) {
    createSourcing(input: $input) {
      id
      sourcingNo
      prIds
      title
      description
      type
      sourcingScope
      invitationMode
      status
      items {
        id
        name
        description
        quantity
        unit
        unitPrice
        totalPrice
        specifications
        tags
        allocations {
          prId
          prItemId
          quantity
        }
      }
      totalAmount
      currency
      notes
      attachments {
        fileId
        originalName
        mimeType
        size
        category
        url
      }
      evaluationCriterias {
        id
        name
        description
        weight
        visibleToSuppliers
        threshold
      }
      createdBy
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_SOURCING_MUTATION = gql`
  mutation UpdateSourcing($id: String!, $input: UpdateSourcingInput!) {
    updateSourcing(id: $id, input: $input) {
      id
      sourcingNo
      prIds
      title
      description
      type
      sourcingScope
      invitationMode
      status
      items {
        id
        name
        description
        quantity
        unit
        unitPrice
        totalPrice
        specifications
        tags
        allocations {
          prId
          prItemId
          quantity
        }
      }
      totalAmount
      currency
      notes
      attachments {
        fileId
        originalName
        mimeType
        size
        category
        url
      }
      evaluationCriterias {
        id
        name
        description
        weight
        visibleToSuppliers
        threshold
      }
      approvalType
      steps {
        order
        roleSlug
        stepName
      }
      externalWorkflow
      updatedAt
    }
  }
`;

export const SUBMIT_SOURCING_MUTATION = gql`
  mutation SubmitSourcing($input: SubmitSourcingInput!) {
    submitSourcing(input: $input) {
      id
      sourcingNo
      status
      submittedBy
      submittedAt
      updatedAt
    }
  }
`;

export const APPROVE_SOURCING_MUTATION = gql`
  mutation ApproveSourcing($input: ApproveSourcingInput!) {
    approveSourcing(input: $input) {
      id
      sourcingNo
      status
      approvedBy
      updatedAt
    }
  }
`;

export const DELETE_SOURCING_MUTATION = gql`
  mutation DeleteSourcing($id: String!) {
    deleteSourcing(id: $id) {
      id
      sourcingNo
    }
  }
`;

// Supplier Portal Queries (via Backend API)
export const SUPPLIER_PORTAL_TEMPLATES_QUERY = gql`
  query GetSupplierPortalTemplates {
    supplierPortalTemplates {
      id
      name
      sourcingType
      biddingType
      fields {
        name
        label
        description
        type
        isRequired
      }
      fileRequirements {
        name
        label
        description
        documentType
        isRequired
      }
    }
  }
`;

export const SUPPLIER_PORTAL_SUPPLIERS_QUERY = gql`
  query GetSupplierPortalSuppliers {
    supplierPortalSuppliers {
      id
      companyName
      description
      contactInfo {
        email
        phone
        address
      }
      services
      industries
      verificationStatus
      profileProgress
      documents {
        id
        documentName
        type
        fileName
        url
      }
    }
  }
`;

export const SUPPLIER_PORTAL_CREATE_SOURCING_MUTATION = gql`
  mutation CreateSourcing($input: CreateSourcingInput!) {
    createSourcing(input: $input) {
      id
      title
      sourcingType
      biddingType
      description
      dueDate
      status
      isPublic
      template {
        id
        name
      }
      invitedSuppliers {
        id
        companyName
      }
      fields {
        name
        label
        description
        type
        isRequired
      }
      fileRequirements {
        name
        label
        description
        documentType
        isRequired
      }
    }
  }
`;

export const SUPPLIER_PORTAL_PUBLISH_SOURCING_MUTATION = gql`
  mutation PublishSourcing($input: SupplierPortalCreateSourcingInput!, $sourcingId: String) {
    publishSupplierPortalSourcing(input: $input, sourcingId: $sourcingId) {
      id
      title
      sourcingType
      biddingType
      description
      dueDate
      status
      isPublic
      template {
        id
        name
      }
      invitedSuppliers {
        id
        companyName
      }
      fields {
        name
        label
        description
        type
        isRequired
      }
      fileRequirements {
        name
        label
        description
        documentType
        isRequired
      }
    }
  }
`;

export const COUNT_BIDS_BY_SOURCING_QUERY = gql`
  query CountBidsBySourcing($sourcingId: String!) {
    countBidsBySourcing(sourcingId: $sourcingId)
  }
`;

export const GET_BIDS_BY_SOURCING_QUERY = gql`
  query GetBidsBySourcing($sourcingId: String!) {
    getBidsBySourcing(sourcingId: $sourcingId)
  }
`;

export const GET_LOCAL_BIDS_BY_SOURCING_QUERY = gql`
  query GetLocalBidsBySourcing($sourcingId: String!) {
    bids(query: { filter: { sourcingId: { eq: $sourcingId } } }) {
      nodes {
        id
        supplierId
        supplierName
        companyName
        items {
          itemName
          description
          quantity
          unit
          unitPrice
          total
          deliveryTime
          notes
          specifications
        }
        submissionDate
        status
        createdAt
        updatedAt
      }
    }
  }
`;

export const OPEN_BIDS_MUTATION = gql`
  mutation OpenBids($input: OpenBidsInput!) {
    openBids(input: $input) {
      id
      publishData
}
  }
`;

export const SAVE_EVALUATION_MUTATION = gql`
  mutation SaveEvaluation($input: SaveEvaluationInput!) {
    saveEvaluation(input: $input) {
      id
      evaluationResults {
        criteriaId
        value
        remarks
      }
    }
  }
`;

export const UPDATE_BID_EVALUATION_MUTATION = gql`
  mutation UpdateBidEvaluation($input: UpdateBidEvaluationInput!) {
    updateBidEvaluation(input: $input) {
      id
      compliance
      decision
      evaluationRemarks
    }
  }
`;

// Evaluation Phase Management Mutations
export const START_TECHNICAL_EVALUATION_MUTATION = gql`
  mutation StartTechnicalEvaluation($input: StartTechnicalEvaluationInput!) {
    startTechnicalEvaluation(input: $input) {
      id
      evaluationPhase
    }
  }
`;

export const COMPLETE_TECHNICAL_EVALUATION_MUTATION = gql`
  mutation CompleteTechnicalEvaluation($input: CompleteTechnicalEvaluationInput!) {
    completeTechnicalEvaluation(input: $input) {
      id
      evaluationPhase
    }
  }
`;

export const START_FINANCIAL_EVALUATION_MUTATION = gql`
  mutation StartFinancialEvaluation($input: StartFinancialEvaluationInput!) {
    startFinancialEvaluation(input: $input) {
      id
      evaluationPhase
    }
  }
`;

export const COMPLETE_FINANCIAL_EVALUATION_MUTATION = gql`
  mutation CompleteFinancialEvaluation($input: CompleteFinancialEvaluationInput!) {
    completeFinancialEvaluation(input: $input) {
      id
      evaluationPhase
    }
  }
`;

export const SUBMIT_RESULTS_MUTATION = gql`
  mutation SubmitResults($input: SubmitResultsInput!) {
    submitResults(input: $input) {
      id
      sourcingNo
      status
      awardApprovalType
      awardApprovalFlow
      awardApprovals {
        order
        roleSlug
        stepName
        approvedBy
        approvedOn
        remarks
        stepStatus
      }
      updatedAt
    }
  }
`;

export const SAVE_ITEM_AWARDS_MUTATION = gql`
  mutation SaveItemAwards($input: SaveItemAwardsInput!) {
    saveItemAwards(input: $input) {
      id
      status
      itemAwards {
        id
        sourcingItemId
        bidId
        supplierId
        supplierName
        quantity
        unitPrice
        totalPrice
        decision
        remarks
      }
    }
  }
`;

export const DOCUMENT_TEMPLATES_QUERY = gql`
  query DocumentTemplates {
    documentTemplates {
      nodes {
        id
        documentName
        category
        tag
        url
      }
    }
  }
`;

export const RENDER_DOCUMENT_TEMPLATE_MUTATION = gql`
  mutation RenderDocumentTemplate($input: RenderDocumentTemplateInput!) {
    renderDocumentTemplate(input: $input)
  }
`;

export const GENERATE_AWARD_LETTER_MUTATION = gql`
  mutation GenerateAwardLetter($sourcingId: String!, $documentTemplateId: String!, $supplierId: String) {
    generateAwardLetter(sourcingId: $sourcingId, documentTemplateId: $documentTemplateId, supplierId: $supplierId) {
      id
      awardLetters {
        fileId
        originalName
        mimeType
        size
        category
        url
        createdBy
      }
      awardLetter {
        fileId
        originalName
        mimeType
        size
        category
        url
        createdBy
      }
    }
  }
`;

export const DELETE_AWARD_LETTER_MUTATION = gql`
  mutation DeleteAwardLetter($sourcingId: String!, $supplierId: String) {
    deleteAwardLetter(sourcingId: $sourcingId, supplierId: $supplierId) {
      id
      awardLetters {
        fileId
        originalName
        mimeType
        size
        category
        url
        createdBy
      }
      awardLetter {
        fileId
        originalName
        mimeType
        size
        category
        url
        createdBy
      }
    }
  }
`;

export const ACCEPT_AWARD_MUTATION = gql`
  mutation AcceptAward($input: AcceptAwardInput!) {
    acceptAward(input: $input) {
      id
      sourcingNo
      status
      awardAcceptanceLetter {
        fileId
        originalName
        mimeType
        size
        category
        url
        createdBy
      }
      awardedBy
      awardedAt
      updatedAt
    }
  }
`;

export const CREATE_PURCHASE_ORDER_FROM_SOURCING_MUTATION = gql`
  mutation CreatePurchaseOrderFromSourcing($input: CreatePurchaseOrderFromSourcingInput!) {
    createPurchaseOrderFromSourcing(input: $input) {
      id
      orderNo
      sourcingId
      status
      totalAmount
      currency
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_PROCUREMENT_MINUTE_MUTATION = gql`
  mutation CreateProcurementMinute($input: CreateProcurementMinuteInput!) {
    createProcurementMinute(input: $input) {
      id
      minuteType
      minuteTitle
      committeeMembers {
        fullName
        role
        organization
        linkedUserId
      }
      templateId
      inputs
      renderedSnapshot
      status
      locked
      preparedBy
      preparedAt
      approvedBy
      approvedAt
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_PROCUREMENT_MINUTE_MUTATION = gql`
  mutation UpdateProcurementMinute($id: ID!, $input: UpdateProcurementMinuteInput!) {
    updateProcurementMinute(id: $id, input: $input) {
      id
      minuteType
      minuteTitle
      committeeMembers {
        fullName
        role
        organization
        linkedUserId
      }
      templateId
      contentType
      documentTemplateId
      inputs
      renderedSnapshot
      generatedDocument {
        fileId
        originalName
        mimeType
        size
        category
        url
        createdBy
      }
      status
      locked
      preparedBy
      preparedAt
      approvedBy
      approvedAt
      createdAt
      updatedAt
    }
  }
`;

export const GET_PROCUREMENT_MINUTES_QUERY = gql`
  query GetProcurementMinutes($sourcingId: String!) {
    procurementMinutes(query: { filter: { sourcingId: { eq: $sourcingId } } }) {
      nodes {
        id
        minuteType
        minuteTitle
        committeeMembers {
          fullName
          role
          organization
          linkedUserId
        }
        templateId
        contentType
        documentTemplateId
        inputs
        renderedSnapshot
        generatedDocument {
          fileId
          originalName
          mimeType
          size
          category
          url
          createdBy
        }
        status
        locked
        preparedBy
        preparedAt
        approvedBy
        approvedAt
        createdAt
        updatedAt
      }
    }
  }
`;

export const DELETE_PROCUREMENT_MINUTE_MUTATION = gql`
  mutation DeleteProcurementMinute($id: ID!) {
    deleteProcurementMinute(id: $id) {
      id
      minuteType
      minuteTitle
      status
      locked
    }
  }
`;

export const GENERATE_MINUTE_DOCUMENT_MUTATION = gql`
  mutation GenerateMinuteDocument($minuteId: String!) {
    generateMinuteDocument(minuteId: $minuteId) {
      id
      minuteType
      minuteTitle
      renderedSnapshot
      inputs
      status
      locked
      preparedBy
      preparedAt
      approvedBy
      approvedAt
      updatedAt
    }
  }
`;
